import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Heading,
  Link,
  Paragraph,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import { useRemindAboutPlanMutation } from '../../../../hooks/queries/hr/useHrWorkEnvironmentOverviewQuery';
import {
  ItemView,
  requirementItems,
  requirementView,
  watchItems,
  watchView,
} from '../Overview/hrOverviewView';
import { OverviewSubpage } from '../Overview/OverviewSubpage';
import { reminderStatus } from '../../../WorkEnvironmentSurveyDetail/reminderView';
import classes from '../Overview/Overview.module.css';

const RemindLeader = ({ surveyId }: { surveyId: number }): ReactElement => {
  const { t, i18n } = useTranslation();
  const remind = useRemindAboutPlanMutation();
  const { sent, failed } = reminderStatus(remind, t, i18n.language);
  if (sent)
    return (
      <span
        className={`ds-paragraph ${classes.done}`}
        data-size="sm"
        role="status"
      >
        {sent}
      </span>
    );
  return (
    <div className={classes.rowText}>
      <Button
        variant="secondary"
        data-size="sm"
        loading={remind.isPending}
        onClick={() => remind.mutate(surveyId)}
      >
        {t('hrOverview.followUp.planOverdue.action')}
      </Button>
      {failed && <ValidationMessage data-size="sm">{failed}</ValidationMessage>}
    </div>
  );
};

const Item = ({ item }: { item: ItemView }): ReactElement => {
  const { t } = useTranslation();
  return (
    <li className={classes.listRow}>
      <div className={`${classes.rowText} ${classes.wideText}`}>
        <span className={classes.itemTitle}>
          <Tag data-size="sm" data-color={item.color}>
            {t(`hrOverview.followUp.${item.kind}.tag`)}
          </Tag>
          <span className="ds-label">{item.team}</span>
        </span>
        <Paragraph data-size="sm">{item.text}</Paragraph>
      </div>
      {item.remind !== null && <RemindLeader surveyId={item.remind} />}
      {item.link && (
        <Link asChild data-size="sm">
          <RouterLink to={item.link}>
            {t(`hrOverview.followUp.${item.kind}.action`)}
          </RouterLink>
        </Link>
      )}
    </li>
  );
};

const ItemsCard = ({
  id,
  items,
}: {
  id: 'requirements' | 'watch';
  items: ItemView[];
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <section aria-labelledby={`${id}-heading`} className={classes.card}>
      <div className={classes.heading}>
        <Heading level={2} data-size="xs" id={`${id}-heading`}>
          {t(`hrOverview.requirements.${id}.title`)}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t(`hrOverview.requirements.${id}.intro`)}
        </Paragraph>
      </div>
      {items.length === 0 ? (
        <Paragraph data-size="sm">
          {t(`hrOverview.requirements.${id}.none`)}
        </Paragraph>
      ) : (
        <ul className={classes.rows}>
          {items.map((item) => (
            <Item key={item.key} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
};

const Requirements = ({
  overview,
}: {
  overview: HrWorkEnvironmentOverview;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <>
      <ItemsCard
        id="requirements"
        items={requirementItems(overview).map((item) =>
          requirementView(item, t, i18n.language),
        )}
      />
      <ItemsCard
        id="watch"
        items={watchItems(overview).map((item) =>
          watchView(item, overview, t, i18n.language),
        )}
      />
    </>
  );
};

export const HRRequirements = (): ReactElement => (
  <OverviewSubpage page="requirements">
    {(overview) => <Requirements overview={overview} />}
  </OverviewSubpage>
);
